import { useRef } from "react";
import { Description, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import type { HeldSession } from "@/common/lib/crossTabSession";

interface RecordingInAnotherTabModalProps {
    isOpen: boolean;
    sessions: HeldSession[];
    onConfirm: () => void;
    onCancel: () => void;
}

// EN-1609: a recording paused or running in another tab would be stranded by a new start here.
// Names the first session; says "paused" if any session is paused. Escape and outside clicks cancel.
const RecordingInAnotherTabModal = ({ isOpen, sessions, onConfirm, onCancel }: RecordingInAnotherTabModalProps) => {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const name = sessions[0]?.patientName || "Another visit";
    const status = sessions.some((s) => s.state === "paused") ? "paused" : "still recording";

    return (
        <Dialog open={isOpen} onClose={onCancel} role="alertdialog" initialFocus={confirmRef} className="relative z-50">
            <div className="fixed inset-0 bg-shadow-950/70" aria-hidden="true" />
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-start justify-center px-4 py-6 sm:items-center">
                    <DialogPanel className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
                        <div className="flex flex-col gap-3.5 px-6 pb-6 pt-5">
                            <DialogTitle className="fraunces-soft m-0 text-[22px] font-semibold leading-7 text-shadow-950">
                                Recording in another tab
                            </DialogTitle>
                            <Description className="m-0 text-[15px] leading-[22px] text-shadow-700">
                                {`${name} is ${status} in another HappyDoc tab. Stop and save it before starting this recording?`}
                            </Description>
                            <button
                                ref={confirmRef}
                                type="button"
                                onClick={onConfirm}
                                className="h-12 rounded-xl bg-max-900 text-[15px] font-semibold text-white hover:bg-max-950"
                            >
                                Stop &amp; save, then record
                            </button>
                            <button
                                type="button"
                                onClick={onCancel}
                                className="h-12 rounded-xl border border-shadow-300 bg-white text-[15px] font-semibold text-shadow-900 hover:bg-shadow-25"
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export default RecordingInAnotherTabModal;
